<template>
  <v-form
    :lazy-validation="true"
    @submit.prevent="handleSubmit"
    v-model="valid"
    ref="form"
  >
    <v-card>
      <v-stepper v-model="step">
        <v-stepper-header>
          <v-stepper-step :complete="step > steps.DEFINE" step="1" editable>
            {{ $t("formula.createSteps.define") }}
          </v-stepper-step>

          <v-stepper-step :complete="step > steps.ASSIGN" step="2">
            {{ $t("formula.createSteps.assign") }}
          </v-stepper-step>

          <v-stepper-step :complete="step >= steps.SUMMARY" step="3">
            {{ $t("formula.createSteps.summary") }}
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-card-text>
              <formula-info-form
                v-model="payload"
                :selectableTags="selectableTags"
              />
            </v-card-text>
            <v-row>
              <v-spacer />
              <v-btn
                color="primary"
                text
                @click="moveStep"
                x-large
                :disabled="!canMoveStep"
              >
                {{ $t("common.next") }}
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-card-text>
              <v-textarea
                :label="$t('formula.fields.formula')"
                autocomplete="off"
                type="text"
                outlined
                v-model="payload.value"
                dense
                disabled
              />
              <v-btn
                color="primary"
                text
                @click="runTest"
                :disabled="!variablesAssigned"
              >
                {{ $t("formula.fields.testButton") }}
              </v-btn>
              <div v-if="testResult?.value">
                <v-col
                  cols="12"
                  md="12"
                  sm="12"
                  class="d-flex flex-column justify-center align-center pa-0 ma-0"
                >
                  <p
                    :class="
                      (testResult.success ? 'green' : 'red') +
                      '--text' +
                      ' ma-0'
                    "
                  >
                    {{
                      testResult.success
                        ? $t("formula.form.runtest") + testResult.value
                        : $t("formula.errors.runtest") + testResult.value
                    }}
                  </p>
                </v-col>
                <v-col
                  cols="12"
                  md="12"
                  sm="12"
                  class="d-flex flex-column justify-center align-center pa-0 ma-0"
                >
                  <p
                    :class="
                      (testResult.success ? 'green' : 'red') +
                      '--text' +
                      ' ma-0'
                    "
                  >
                    {{
                      testResult.success
                        ? $t("formula.form.testFormula") +
                          testResult.calculatedFormula
                        : $t("formula.form.testFormula") +
                          testResult.calculatedFormula
                    }}
                  </p>
                </v-col>
              </div>

              <formula-resource-selection
                v-model="payload"
                :variableNames="variableNames"
                :selectableTags="selectableTags"
              />
            </v-card-text>
            <v-row>
              <v-spacer></v-spacer>
              <v-btn color="grey" text @click="moveBackStep" x-large>
                <v-icon left>mdi-arrow-left</v-icon>
                {{ $t("common.previous") }}
              </v-btn>

              <v-btn
                color="primary"
                text
                @click="moveStep"
                x-large
                :disabled="!canMoveStep"
              >
                {{ $t("common.next") }}
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="3">
            <v-card-text>
              <v-switch
                v-model="payload.active"
                :label="$t('formula.form.active')"
              />

              <formula-info-form
                v-model="payload"
                :selectableTags="selectableTags"
                disabled
              />

              <formula-resource-selection
                v-model="payload"
                :selectableTags="selectableTags"
                disabled
              />
            </v-card-text>
            <v-row>
              <v-spacer></v-spacer>
              <v-btn color="grey" text @click="moveBackStep" x-large>
                <v-icon left>mdi-arrow-left</v-icon>
                {{ $t("common.previous") }}
              </v-btn>
            </v-row>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-card-actions>
        <v-btn
          color="primary"
          type="button"
          @click="handleSubmit"
          x-large
          :disabled="!canSave"
        >
          {{ edit ? $t("common.update") : $t("common.create") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { mapActions } from "vuex";
import FormulaInfoForm from "./FormulaInfoForm.vue";
import FormulaResourceSelection from "./FormulaResourceSelection.vue";
const steps = {
  DEFINE: 1,
  ASSIGN: 2,
  SUMMARY: 3,
};

export default {
  name: "FormulaForm",

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },

    edit: {
      type: Boolean,
      default: false,
    },

    givenOutput: {
      type: Boolean,
      default: false,
    },
  },

  model: {
    prop: "value",
    event: "input",
  },

  data() {
    return {
      step: 1,
      valid: false,
      selectableTags: [],
      testResult: {},

      variableNames: [],
      payload: {
        name: "",
        description: "",
        value: "",
        awaitAllInput: false,
        useTimestampFromTrigger: false,
        active: true,
        outputDeveui: "",
        outputKey: "",

        resources: [],
      },
    };
  },

  components: {
    FormulaInfoForm,
    FormulaResourceSelection,
  },

  computed: {
    steps() {
      return steps;
    },

    canSave() {
      return this.step >= steps.SUMMARY;
    },

    canMoveStep() {
      switch (this.step) {
        case steps.DEFINE:
          return (
            this.payload.name &&
            this.payload.value &&
            this.payload.outputDeveui &&
            this.payload.outputKey
          );
        case steps.ASSIGN:
          return this.variablesAssigned;
        default:
          return true;
      }
    },

    variablesAssigned() {
      return this.payload.resources.every(
        (x) => (x.deveui && x.key) || x.constantId
      );
    },
  },

  methods: {
    ...mapActions("formulas", [
      "getFormulaTemplates",
      "getVariablesFromFormulaString",
      "testFormula",
      "createOrUpdateFormula",
    ]),
    ...mapActions("tag", ["getTagsWithInformation"]),
    ...mapActions("alert", ["success", "error"]),

    async handleSubmit() {
      if (!this.$refs.form.validate()) {
        this.valid = false;
        console.error(
          "form validation error",
          this.$refs.form.errors,
          this.payload
        );
        return;
      }

      let test = await this.runTest(false);
      if (!test.success) {
        this.error(this.$t("formula.errors.runtest") + test.value);
        return;
      }

      let result = await this.createOrUpdateFormula(this.payload);
      if (result) {
        this.edit
          ? this.success(this.$t("formula.form.updateFormula"))
          : this.success(this.$t("formula.form.createFormula"));
        this.$emit("update");
        return;
      }
      this.error(this.$t("formula.errors.createFormula"));
    },

    moveStep() {
      this.step++;
    },

    moveBackStep() {
      this.step--;
    },

    async runTest(notification = true) {
      let res = await this.testFormula(this.payload);
      if (notification) {
        this.testResult = res;
        res.success
          ? this.success(this.$t("formula.form.runtest") + res.value)
          : this.error(this.$t("formula.errors.runtest") + res.value);
      }
      return res;
    },
  },

  watch: {
    //Watcher to calculate the variables needed in the given formula when going into the resource assignment step
    step: {
      async handler(value) {
        switch (value) {
          case steps.ASSIGN:
            this.variableNames = await this.getVariablesFromFormulaString(
              this.payload.value
            );
            break;
        }
      },
    },
  },

  mounted() {
    this.$refs.form.resetValidation();
    if (this.edit) this.step = steps.SUMMARY;
  },

  async created() {
    if (this.givenOutput) {
      this.payload.outputDeveui = this.value?.outputDeveui ?? "";
      this.payload.outputKey = this.value?.outputKey ?? "";
    }

    await this.getFormulaTemplates();
    this.selectableTags = await this.getTagsWithInformation();

    if (this.edit) {
      if (this.value?.formulaId == undefined) {
        this.error(this.$t("formula.errors.gettingData"));
        this.$router.push("/formula");
        return;
      }
      this.payload = this.value;
    }
  },
};
</script>
